import ChevronDownIcon from "@redotech/redo-icons/chevron-down.svg";
import { KEYS } from "platejs";
import { useEditorRef, useEditorSelector } from "platejs/react";
import { memo, useCallback, useState } from "react";
import { RedoIcon } from "../../arbiter-components/icon/redo-icon";
import { cn } from "../../tailwind-utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { EDITOR_OVERLAY_Z_CLASS } from "./editor-overlay";
import { useToolbarOverflow } from "./toolbar-overflow";

const FONT_FAMILIES = [
  { label: "Default", value: "" },
  { label: "Arial", value: "Arial, sans-serif" },
  { label: "Georgia", value: "Georgia, serif" },
  { label: "Times New Roman", value: "'Times New Roman', serif" },
  { label: "Courier New", value: "'Courier New', monospace" },
  { label: "Verdana", value: "Verdana, sans-serif" },
  { label: "Trebuchet MS", value: "'Trebuchet MS', sans-serif" },
  { label: "Tahoma", value: "Tahoma, sans-serif" },
] as const;

export const FontFamilyToolbarButton = memo(function FontFamilyToolbarButton() {
  const editor = useEditorRef();
  const [open, setOpen] = useState(false);
  const { isOverflow } = useToolbarOverflow();

  const currentFont = useEditorSelector((editor) => {
    const value = editor.api.mark(KEYS.fontFamily);
    return typeof value === "string" ? value : "";
  }, []);

  const currentLabel =
    FONT_FAMILIES.find((f) => f.value === currentFont)?.label || "Default";

  const handleSelect = useCallback(
    (value: string) => {
      if (value) {
        editor.tf.addMarks({ [KEYS.fontFamily]: value });
      } else {
        editor.tf.removeMarks(KEYS.fontFamily);
      }
      editor.tf.focus();
      setOpen(false);
    },
    [editor],
  );

  if (isOverflow) {
    return (
      <DropdownMenuSub>
        <DropdownMenuSubTrigger inset>Font family</DropdownMenuSubTrigger>
        <DropdownMenuSubContent className={EDITOR_OVERLAY_Z_CLASS}>
          {FONT_FAMILIES.map((font) => (
            <DropdownMenuItem
              key={font.label}
              onSelect={() => handleSelect(font.value)}
              style={{ fontFamily: font.value || undefined }}
            >
              {font.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    );
  }

  return (
    <DropdownMenu modal={false} onOpenChange={setOpen} open={open}>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            "inline-flex h-6 items-center gap-1 rounded-sm px-2 text-xs text-foreground",
            "hover:bg-accent hover:text-accent-foreground",
          )}
          type="button"
        >
          <span className="max-w-[80px] truncate">{currentLabel}</span>
          <RedoIcon Icon={ChevronDownIcon} size={12} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className={EDITOR_OVERLAY_Z_CLASS}>
        {FONT_FAMILIES.map((font) => (
          <DropdownMenuItem
            key={font.label}
            onSelect={() => handleSelect(font.value)}
            style={{ fontFamily: font.value || undefined }}
          >
            <span className="text-sm">{font.label}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
});
